import React from "react";
import { ICON_NAMES } from "@/components/Icon";

/** Generic editable text input row */
export function TextField({ label, value, onChange, multiline = false, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder={placeholder}
          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy"
          data-testid={testid}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy"
          data-testid={testid}
        />
      )}
    </div>
  );
}

/** Icon picker as a dropdown */
export function IconPicker({ label = "Icon", value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
        {label}
      </label>
      <select
        value={value || "Target"}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy bg-white"
        data-testid={testid}
      >
        {ICON_NAMES.map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
    </div>
  );
}

/** Color picker with text fallback */
export function ColorField({ label = "Color", value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
        {label}
      </label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={value || "#0a1f5c"}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-10 border border-slate-200 rounded-lg cursor-pointer"
          data-testid={`${testid}-color`}
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:border-navy"
          data-testid={testid}
          placeholder="#0a1f5c"
        />
      </div>
    </div>
  );
}

/** Bullets list editor (array of strings) */
export function BulletsField({ label = "Bullets", value, onChange, testid }) {
  const list = Array.isArray(value) ? value : [];
  const setAt = (i, v) => {
    const next = [...list]; next[i] = v; onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, ""]);
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{label}</label>
      <div className="space-y-2">
        {list.map((b, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={b}
              onChange={(e) => setAt(i, e.target.value)}
              className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"
              data-testid={`${testid}-${i}`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-red-500 hover:text-red-700 px-2"
              aria-label="Remove bullet"
              data-testid={`${testid}-remove-${i}`}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="text-xs font-semibold text-navy hover:text-gold-deep px-3 py-1.5 rounded-lg border border-slate-200"
          data-testid={`${testid}-add`}
        >
          + Add bullet
        </button>
      </div>
    </div>
  );
}
